import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { PalmettoButton, PalmettoToggleGroup, type PalmettoToggleGroupItemProps } from '@palmetto/dsm-components';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

const VIEW_PATHS: Record<string, string> = {
  templates: '/admin/checklists',
  systemLossThresholds: '/admin/checklists/system-loss-thresholds',
};

const VIEW_ITEMS: PalmettoToggleGroupItemProps[] = [
  { value: 'templates', label: 'Templates' },
  { value: 'systemLossThresholds', label: 'System Loss Thresholds' },
];

interface ChecklistAdminLayoutProps {
  children: ReactNode;
}

export const ChecklistAdminLayout = ({ children }: ChecklistAdminLayoutProps) => {
  const navigate = useNavigate();
  const isThresholdsView = useMatch('/admin/checklists/system-loss-thresholds') !== null;
  const activeView = isThresholdsView ? 'systemLossThresholds' : 'templates';
  const activeViewValue = useMemo(() => [activeView], [activeView]);

  const handleViewChange = useCallback(
    (details: { value: string[] }) => {
      const [nextView] = details.value;
      const nextPath = VIEW_PATHS[nextView];

      if (nextPath && nextView !== activeView) {
        navigate(nextPath);
      }
    },
    [activeView, navigate],
  );

  const handleNewTemplate = useCallback(() => navigate(`/admin/checklists/new`), [navigate]);

  return (
    <DsmPageContainer title="Checklist">
      <PageHeader title="Checklist">
        {!isThresholdsView && (
          <PalmettoButton emphasis="primary" onClick={handleNewTemplate}>
            + New Template
          </PalmettoButton>
        )}
      </PageHeader>
      <div className="mx-6 mb-16">
        <PalmettoToggleGroup
          value={activeViewValue}
          onValueChange={handleViewChange}
          items={VIEW_ITEMS}
          scrollActiveIntoView={false}
        />
      </div>
      <div className="mx-6">{children}</div>
    </DsmPageContainer>
  );
};
